import React, { useState } from 'react';
import { Box, Link, TextField, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

/**
 * Inspector for a python_code node: names the linked code module, links to it
 * when the viewer can see it, and edits the node's `params` overrides as JSON.
 * Render it with `key={nodeId}` so switching nodes resets the text.
 */
const CodeNodeConfig = ({ config, modules = [], canEdit, onConfigChange }) => {
  const moduleId = config?.code_module_id;
  const linked = modules.find((m) => m.custom_module_id === moduleId);
  const [text, setText] = useState(() => JSON.stringify(config?.params || {}, null, 2));
  const [invalid, setInvalid] = useState(false);

  const handleChange = (e) => {
    setText(e.target.value);
    try {
      const value = JSON.parse(e.target.value);
      if (value === null || typeof value !== 'object' || Array.isArray(value)) throw new Error('not an object');
      setInvalid(false);
      onConfigChange({ ...config, params: value });
    } catch {
      setInvalid(true);
    }
  };

  return (
    <Box>
      <Typography variant="subtitle2">Code module</Typography>
      {linked ? (
        <Typography variant="body2" sx={{ mb: 2 }}>
          {linked.name}{' '}
          <Link component={RouterLink} to={`/modules?code_module=${moduleId}`}>Open</Link>
        </Typography>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {moduleId ? `Code module #${moduleId} (not visible to you)` : 'No code module linked'}
        </Typography>
      )}
      <TextField
        label="Params (JSON)"
        value={text}
        onChange={handleChange}
        fullWidth
        multiline
        minRows={3}
        disabled={!canEdit}
        error={invalid}
        helperText={invalid ? 'Must be a JSON object' : "Overrides the module's default params"}
        InputProps={{ sx: { fontFamily: 'monospace' } }}
      />
    </Box>
  );
};

export default CodeNodeConfig;
